import React, { useRef } from "react";
import { Icon } from "react-feather";
import { NavLink } from "react-router-dom";
import styled from "styled-components";

interface Props {
  tabs: {
    key: string;
    label: React.ReactNode;
    color?: string;
  }[];
  icon?: Icon;
  children?: React.ReactNode;
  value?: string;
  onChange?: (value: string) => void;
  link?: boolean;
  className?: string;
}

const TabWrapper = styled.div`
  margin-right: auto;
  background-color: var(--dark200);
  border-bottom: 1px solid var(--dark100);
  display: flex;
  align-items: center;
  padding: 0 10px;
  flex-wrap: wrap;
  width: 100%;

  > svg {
    color: var(--dark700);
    margin-right: 20px;
  }
`;

/** Exported (not just used internally) so a consumer that needs the SAME tab-button look/active-
 *  state styling outside `Tabs`'s own `TabWrapper` layout can reuse it directly instead of forking
 *  the CSS — e.g. `SpotterHeader.tsx`'s mobile carousel-tabs variant, which needs individual tab
 *  buttons inside a horizontally-scrolling row instead of `TabWrapper`'s flex-wrap one. */
export const Tab = styled.button<{ $color?: string }>`
  border: none;
  text-transform: none;
  border-radius: 0;
  color: ${(props) => props.$color ?? "var(--gray300)"};
  padding: 5px 10px 8px;
  padding-top: 7px;
  font-size: 16px;
  display: flex;
  align-items: center;
  min-height: 35px;
  background: none;
  font-weight: inherit;
  border-radius: 5px;
  position: relative;

  &:hover {
    border: none;
    background-color: var(--dark300);
    box-shadow: none;
  }

  &.active {
    color: ${(props) => props.$color ?? "var(--blue300)"};

    &::after {
      content: "";
      background-color: ${(props) => props.$color ?? "var(--blue300)"};
      width: 100%;
      position: absolute;
      left: 0;
      bottom: 0;
      height: 3px;
      display: block;
      border-radius: 2px;
    }
  }
`;

const Tabs = (props: Props) => {
  const { tabs, icon, children, value, onChange, link, className } = props;
  const tabRefs = useRef<(HTMLElement | null)[]>([]);

  return (
    <TabWrapper className={className}>
      {icon && React.createElement(icon, { size: 19 })}
      {children}
      {tabs?.map((tab, index) => (
        <React.Fragment key={tab.key}>
          {link ? (
            <Tab
              as={NavLink}
              to={tab.key}
              onClick={() => {
                tabRefs.current[index]?.blur();
              }}
              ref={(element) => (tabRefs.current[index] = element)}
            >
              {tab.label}
            </Tab>
          ) : (
            <Tab
              className={value === tab.key ? "active" : undefined}
              onClick={() => {
                onChange?.(tab.key);
                tabRefs.current[index]?.blur();
              }}
              ref={(element) => (tabRefs.current[index] = element)}
              $color={tab.color}
            >
              {tab.label}
            </Tab>
          )}
        </React.Fragment>
      ))}
    </TabWrapper>
  );
};

export default Tabs;
